import { useId, useState } from "react";
import { MdCloudUpload, MdDelete, MdFileDownload, MdInsertDriveFile } from "react-icons/md";
import AtriumApi, { getApiErrorMessage, getSubdomain } from "api";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import notify from "lib/notify";
import Button, { IconButton } from "components/button";
import Dialog from "components/dialog";
import { formatNumber } from "utils/format";

const uploadErrorMessage = (error) => {
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0 && errors.every((e) => typeof e === "string")) {
    return errors.join(", ");
  }
  return getApiErrorMessage(error, "Could not import people");
};

const downloadTemplate = () => {
  const link = document.createElement("a");
  link.href = "/user-template.csv";
  link.download = "user-template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const UploadCsvDialog = ({ openCsvDialog, setOpenCsvDialog, getUsers }) => {
  const inputId = useId();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const close = () => setOpenCsvDialog(false);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    track(EVENTS.CSV_IMPORT_STARTED);
  };

  const handleUpload = async () => {
    if (!uploadedFile) {
      notify.error("Choose a CSV file first.");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", uploadedFile);
    const subdomain = getSubdomain();
    if (subdomain) {
      formData.append("x-church-slug", subdomain);
    }

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    try {
      const response = await AtriumApi.post("/v1/read-data-from-csv", formData, config);
      notify.success(response?.data?.message || "People imported");
      close();
      await getUsers();
    } catch (error) {
      console.error("Error uploading data:", error);
      notify.error(uploadErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      isOpen={openCsvDialog}
      onClose={close}
      title="Import People from CSV"
      description="Upload a .csv file with fewer than 950 records, up to 50MB."
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={handleUpload} loading={uploading} disabled={!uploadedFile}>
            {uploading ? "Importing…" : "Import"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-3 rounded-card border border-dashed border-line bg-surface-raised p-6 text-center">
          <MdCloudUpload aria-hidden="true" className="h-10 w-10 text-fg-muted" />
          <p className="text-sm text-fg-muted">Only .csv files are supported.</p>
          <label
            htmlFor={inputId}
            className="inline-flex h-8 cursor-pointer items-center rounded-control border border-line bg-surface px-3 text-xs font-medium text-fg transition-colors hover:bg-surface-subtle focus-within:ring-2 focus-within:ring-focus"
          >
            {uploadedFile ? "Choose Another File" : "Browse"}
            <input
              id={inputId}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="sr-only"
            />
          </label>
        </div>

        {uploadedFile && (
          <div className="flex items-center justify-between gap-3 rounded-control border border-line px-4 py-2">
            <div className="flex min-w-0 items-center gap-3">
              <MdInsertDriveFile aria-hidden="true" className="h-5 w-5 shrink-0 text-fg-muted" />
              <div className="min-w-0 text-sm">
                <p className="truncate font-medium text-fg">{uploadedFile.name}</p>
                <p className="text-fg-muted">
                  {formatNumber(uploadedFile.size / 1024, { maximumFractionDigits: 2 })} KB
                </p>
              </div>
            </div>
            <IconButton
              icon={<MdDelete className="h-5 w-5" />}
              label="Remove file"
              size="sm"
              className="text-danger-fg"
              onClick={() => setUploadedFile(null)}
            />
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          leftIcon={<MdFileDownload className="h-4 w-4" />}
          onClick={downloadTemplate}
          className="self-center"
        >
          Download CSV Template
        </Button>
      </div>
    </Dialog>
  );
};

export default UploadCsvDialog;
